/**
 * Food-log entry persistence schema.
 *
 * Invariants:
 * - Entries are timestamped with local time-at + timezone and creation/update stamps.
 * - `foodServingId` must reference a valid serving snapshot.
 *
 * @see `packages/core/docs/database.md`
 */
import { doublePrecision, index, pgEnum, pgTable, text, uuid } from "drizzle-orm/pg-core";
import { relations } from "drizzle-orm";
import { id, temporalInstant, temporalTimestamps } from "../../utils/sql";
import { foodServing } from "../food/food.sql";
import { user } from "../../auth/core/user.sql";
import { foodLogSourceTypes } from "./constants";

export const foodLogSourceType = pgEnum("food_log_source_type", foodLogSourceTypes);

export const foodLogSourceGroup = pgTable(
  "food_log_source_group",
  {
    ...id,
    ...temporalTimestamps,
    timeAt: temporalInstant("time_at").notNull(),
    timeZone: text("time_zone").notNull(),
    userId: uuid("user_id")
      .references(() => user.id)
      .notNull(),
    sourceType: foodLogSourceType("source_type").notNull(),
    sourceId: uuid("source_id").notNull().unique(),
  },
  (t) => [index("food_log_source_group_user_time_at").on(t.userId, t.timeAt)],
);

export const foodLogSourceGroupRelations = relations(foodLogSourceGroup, ({ many }) => ({
  entries: many(foodLogEntry),
}));

export const foodLogEntry = pgTable(
  "food_log_entry",
  {
    ...id,
    ...temporalTimestamps,
    timeAt: temporalInstant("time_at").notNull(),
    timeZone: text("time_zone").notNull(),
    userId: uuid("user_id")
      .references(() => user.id)
      .notNull(),
    sourceGroupId: uuid("source_group_id").references(() => foodLogSourceGroup.id),
    foodServingId: uuid("food_serving_id")
      .notNull()
      .references(() => foodServing.id),
    amount: doublePrecision("amount").notNull(),
  },
  (t) => [index("food_log_entry_time_at").on(t.userId, t.timeAt)],
);

export const foodLogEntryRelations = relations(foodLogEntry, ({ one }) => ({
  sourceGroup: one(foodLogSourceGroup, {
    fields: [foodLogEntry.sourceGroupId],
    references: [foodLogSourceGroup.id],
  }),
  serving: one(foodServing, {
    fields: [foodLogEntry.foodServingId],
    references: [foodServing.id],
  }),
}));
